import { cn } from "@/lib/utils";
import { ACCOUNT_PRIMARY_CTA } from "@/components/onboarding/account-shell";

/**
 * V2-THEME-PLANS U0 — the class strings the V2 frame is built from, lifted
 * out of `app/(site)/auth/auth-themed-shell.tsx` so the sign-in page and the
 * plan/upgrade screens share one copy instead of each approximating it.
 *
 * Colours come from the `auth-*` tokens (`@akili/theme/auth-tokens.css`),
 * which already switch under `.dark`; the explicit `dark:` variants below are
 * the ones the sign-in shell carries for the on-ink text roles.
 */

/** The quiet text link in the nav (Pricing, Docs, Manage billing). Hidden below `sm`. */
export const V2_NAV_LINK =
  "text-auth-mute hover:text-auth-text dark:text-auth-on-ink-mute dark:hover:text-auth-on-ink hidden rounded-full px-3 py-2 text-sm font-medium sm:block";

/** The one solid ink pill in the nav (Start free, Sign in, Back to workspace). */
export const V2_NAV_PILL =
  "bg-auth-ink text-auth-on-ink dark:bg-white dark:text-auth-ink rounded-full px-5 py-2.5 text-sm font-semibold hover:opacity-90";

/** The theme toggle's border and text roles in the nav. */
export const V2_THEME_TOGGLE =
  "border-auth-glass-line text-auth-mute hover:text-auth-text dark:border-auth-glass-line-inverted dark:text-auth-on-ink-mute dark:hover:text-auth-on-ink";

/** The stage: light field, and the navy-to-violet gradient in dark mode. Passed to `AuthShell`. */
export const V2_STAGE =
  "bg-auth-field text-auth-text dark:text-auth-on-ink dark:bg-[linear-gradient(160deg,var(--auth-stage-from)_0%,var(--auth-stage-to)_100%)]";

/** The headline's accent: the iris → cyan → iris gradient clipped to the text. */
export const V2_ACCENT =
  "bg-[linear-gradient(90deg,var(--auth-iris),var(--auth-glow-cyan),var(--auth-iris))] bg-clip-text text-transparent";

/** Muted secondary copy on the stage. */
export const V2_MUTE = "text-auth-mute dark:text-auth-on-ink-mute";

/**
 * The gradient call to action. The gradient itself is the account flow's
 * `ACCOUNT_PRIMARY_CTA` (one source for the brand gradient); V2 adds the
 * auth control radius and the control's type and spacing.
 */
export const V2_PRIMARY_CTA = cn(
  ACCOUNT_PRIMARY_CTA,
  "rounded-auth-control inline-flex w-full items-center justify-center gap-2 border border-transparent px-4 py-3.5",
  "font-auth-sans text-[15.5px] font-semibold tracking-[-0.01em]",
  "focus-visible:outline-auth-indigo focus-visible:outline-2 focus-visible:outline-offset-2",
  "disabled:cursor-not-allowed",
);

/** The secondary button, matching `AuthButton variant="outline"` on the light field, with dark roles. */
export const V2_OUTLINE_BUTTON = cn(
  "rounded-auth-control inline-flex w-full items-center justify-center gap-2 border px-4 py-3.5",
  "font-auth-sans text-[15.5px] font-semibold tracking-[-0.01em] transition-colors",
  "border-auth-line bg-auth-card text-auth-text hover:bg-auth-field",
  "dark:border-auth-glass-line-inverted dark:bg-white/5 dark:text-auth-on-ink dark:hover:bg-white/10",
  "focus-visible:outline-auth-indigo focus-visible:outline-2 focus-visible:outline-offset-2",
  "disabled:cursor-not-allowed disabled:opacity-50",
);
